import { appEvents } from '@app/icare/client/appEvents'
import { authClient, useAuth } from '@app/icare/client/auth'
import { useAppState } from '@app/icare/state/app'
import { isDevOrTest } from '@pkg/shared/env'
import { logger } from '@pkg/shared/logger'
import { time } from '@pkg/shared/utils/time.utils'
import { isServer } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { PropsWithChildren, ReactNode } from 'react'
import { useCallback, useEffect } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { LockOverlay } from './LockOverlay'

export interface AuthGateProps extends PropsWithChildren {
  idleTimeout?: number
  autoLockDisabled?: boolean
  authPage: ReactNode
}

const defaultIdleTimeout = time.minutes(30)

function AuthGate(props: AuthGateProps) {
  const { children, idleTimeout = defaultIdleTimeout, autoLockDisabled = isDevOrTest, authPage } = props
  const { session, status } = useAuth()
  const locked = useAppState(s => s.locked)
  const setLocked = useAppState(s => s.setLocked)

  const onIdle = useCallback(() => {
    if (status === 'unauthenticated') return
    if (autoLockDisabled) {
      logger.warn('Auto lock disabled, would lock due to inactivity')
      return
    }
    logger.info({ idleTimeout }, 'Locking session due to inactivity')
    setLocked(true)
  }, [autoLockDisabled, idleTimeout, status, setLocked])

  const handleActivity = useCallback(() => {
    appEvents.emit('auth.activity', { timestamp: new Date() })
  }, [])

  useIdleTimer({
    crossTab: true,
    syncTimers: 1000,
    name: 'idle-lock',
    timeout: idleTimeout,
    debounce: 2000,
    onAction: handleActivity,
    onIdle,
  })

  useEffect(() => {
    const expiresAt = session?.session?.expiresAt
    if (!expiresAt) return

    const checkExpiry = () => {
      if (dayjs(expiresAt).isBefore(dayjs())) {
        logger.warn({ expiresAt }, 'Session expired, signing out')
        void authClient.signOut()
        appEvents.emit('session.expired', {})
        return true
      }
      return false
    }

    if (checkExpiry()) return
    const interval = setInterval(checkExpiry, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [session])

  if (isServer || status === 'authenticated') {
    return locked ? <LockOverlay onUnlock={() => setLocked(false)} /> : children
  }
  return authPage
}

export default AuthGate
